/*
  Countdown bar
  -------------
  Extends the existing <weekly-preorder-countdown> element so the weekly
  schedule and the batch number come from ONE implementation. That element
  already handles the Monday->Sunday 23:59 cycle in the store timezone, the
  DST-safe UTC solve, the Monday 00:00-00:01 pause and the cycle numbering.
  Only the rendering differs here: segmented numbers instead of a sentence.

  weekly-preorder-countdown.js must load first. Both are deferred classic
  scripts, so document order is preserved.
*/
(function () {
  var Base = customElements.get('weekly-preorder-countdown');
  if (!Base || customElements.get('preorder-countdown-bar')) return;

  class PreorderCountdownBar extends Base {
    connectedCallback() {
      this.timerEl = this.querySelector('.js-countdown-bar-timer');
      this.textEl = this.querySelector('.js-countdown-bar-text');
      this.showLabels = this.dataset.showLabels === 'true';
      this.customEnd = this.parseCustomEnd();
      this.customText = (this.dataset.customText || '').trim();
      // Central override (theme settings -> Weekly shipping countdown). When it is
      // on, its copy wins over this section's own text so every countdown surface
      // in the theme shows the same unified information.
      this.overrideOn = (this.dataset.overrideActive || '').trim() === 'true';
      this.overrideText = this.overrideOn
        ? ((this.dataset.overrideMessage || '').trim() || (this.dataset.overrideWindowLabel || '').trim())
        : '';
      this.reportInput();
      this.update();
      this.start();
    }

    /* Base ticks once a minute, which is right for a week-long countdown. */

    /*
      Accepts what a merchant actually types, not just the documented format.
      Year-first is unambiguous; day-first is read the European way, matching
      the store's locale. Anything else is reported rather than ignored --
      silently falling back to the weekly timer looks like a broken setting.
    */
    parseDateParts(raw) {
      var value = (raw || '').trim();
      if (!value) return null;

      var parts = value.match(/^(\d{4})[-/. ](\d{1,2})[-/. ](\d{1,2})$/);
      if (parts) return this.validDate(+parts[1], +parts[2], +parts[3]);

      parts = value.match(/^(\d{1,2})[-/. ](\d{1,2})[-/. ](\d{4})$/);
      if (parts) return this.validDate(+parts[3], +parts[2], +parts[1]);

      return null;
    }

    validDate(year, month, day) {
      if (month < 1 || month > 12 || day < 1 || day > 31) return null;
      var probe = new Date(Date.UTC(year, month - 1, day));
      if (probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return null;
      return { year: year, month: month, day: day };
    }

    parseTimeParts(raw) {
      var value = (raw || '').trim();
      if (!value) return null;
      var parts = value.match(/^(\d{1,2})[:.h](\d{2})$/i);
      if (!parts) return null;
      var hour = +parts[1];
      var minute = +parts[2];
      if (hour > 23 || minute > 59) return null;
      return { hour: hour, minute: minute };
    }

    parseCustomEnd() {
      var date = this.parseDateParts(this.dataset.endDate);
      this.dateRejected = !date && (this.dataset.endDate || '').trim() !== '';

      var rawTime = (this.dataset.endTime || '').trim();
      var time = this.parseTimeParts(rawTime);
      this.timeRejected = !time && rawTime !== '';

      if (!date) return null;
      return this.zonedTimeToUtc(
        date.year,
        date.month,
        date.day,
        time ? time.hour : 23,
        time ? time.minute : 59,
        0
      );
    }

    /* Theme Editor only: say so instead of quietly using the weekly timer. */
    reportInput() {
      var el = this.querySelector('.js-countdown-bar-notice');
      if (!el) return;
      var messages = [];
      if (this.dateRejected) {
        messages.push('Could not read the end date "' + this.dataset.endDate
          + '", so the weekly cycle is being used. Expected 2026-09-05 or 05/09/2026.');
      }
      if (this.timeRejected) {
        messages.push('Could not read the end time "' + this.dataset.endTime
          + '", so 23:59 is being used. Expected 17:00.');
      }
      if (!messages.length) {
        el.hidden = true;
        return;
      }
      el.hidden = false;
      el.textContent = 'Countdown bar: ' + messages.join(' ');
    }

    update() {
      var schedule = this.getSchedule(new Date());
      var now = Date.now();

      // The date and the text override independently: a custom date keeps the
      // automatic batch label, a custom text keeps the weekly timer.
      var remainingMinutes = this.customEnd !== null
        ? Math.floor((this.customEnd - now) / 60000)
        : schedule.remainingMinutes;

      if (this.customEnd === null && !schedule.isActive) {
        this.hidden = true;
        return;
      }

      if (remainingMinutes < 0) {
        this.hidden = this.dataset.hideOnEnd === 'true';
        remainingMinutes = 0;
      } else {
        this.hidden = false;
      }

      if (this.textEl) {
        this.textEl.textContent = this.overrideText || this.customText || this.buildWindowLabel(schedule.startDate);
      }
      if (this.timerEl) {
        this.renderSegments(remainingMinutes);
      }
    }

    renderSegments(totalMinutes) {
      var days = Math.floor(totalMinutes / 1440);
      var hours = Math.floor((totalMinutes % 1440) / 60);
      var mins = Math.max(totalMinutes % 60, 0);

      var units = [
        [days, this.dataset.labelDayShort || 'd', false],
        [hours, this.dataset.labelHourShort || 'h', true],
        [mins, this.dataset.labelMinuteShort || 'm', true]
      ];

      var html = '';
      for (var i = 0; i < units.length; i += 1) {
        if (i) html += '<span class="countdown-bar__pipe" aria-hidden="true">|</span>';
        var value = units[i][2] ? String(units[i][0]).padStart(2, '0') : String(units[i][0]);
        html += '<span class="countdown-bar__item">'
          + '<span class="countdown-bar__number">' + value + '</span>'
          + (this.showLabels ? '<span class="countdown-bar__unit">' + units[i][1] + '</span>' : '')
          + '</span>';
      }
      this.timerEl.innerHTML = html;
    }
  }

  customElements.define('preorder-countdown-bar', PreorderCountdownBar);
})();
